// draft-card widget — α.6 of the outreach-agent slice.
//
// Rendered inline in the outreach chat dock when the agent emits an
// `outreach.draft.saved` event AND a matching
// `:::widget[draft-card]{...}:::` directive in its text reply. The card
// shows the channel, optional subject, body preview, and an optional
// rationale (one sentence on tone choice from the model). The single
// CTA is "Use this draft" — clicking it:
//   1. Calls useComposerStore.loadDraft() with subject + body_preview
//   2. Writes scope to sessionStorage + navigates to /outreach
//      (H.2.4: /outreach/prospects/:id route is retired; OutreachLayout
//      reads the stored scope on mount and flips the scope chip)
// The Composer (lifted to read from useComposerStore in this same slice)
// then surfaces the populated subject + body. The founder still owns
// the Send click; the agent never sends.
//
// pendingDraftId is set by loadDraft so the future send-event chain
// (α.7+) can reference the draftId in the outgoing outreach.send.*
// envelope. Today it's stored but unused beyond that round-trip.
//
// Tokens: bg-card, border-border, text-foreground-mute — all real
// tailwind tokens declared in apps/web/src/styles/tailwind.css. No hex.

import { useNavigate } from 'react-router-dom';
import { useComposerStore } from '../../lib/use-composer-store';

interface Props {
  prospectId: string;
  draftId: string;
  channel: string;
  subject?: string;
  body_preview: string;
  rationale?: string;
}

export function DraftCard(props: Props) {
  const { prospectId, draftId, channel, subject, body_preview, rationale } =
    props;
  const navigate = useNavigate();
  const loadDraft = useComposerStore((s) => s.loadDraft);

  const onUse = () => {
    loadDraft({ draftId, subject, body: body_preview });
    // H.2.4: /outreach/prospects/:id retired. Write scope to sessionStorage
    // so OutreachLayout picks it up on mount and sets the scope chip.
    sessionStorage.setItem('outreach-scope', JSON.stringify({ prospectId }));
    navigate('/outreach');
  };

  return (
    <div
      data-testid="draft-card"
      data-prospect-id={prospectId}
      data-draft-id={draftId}
      className="my-2 rounded-md border border-border bg-card p-3"
    >
      <div className="text-xs text-foreground-mute">{channel} draft</div>
      {subject ? (
        <div className="mt-1 text-sm font-semibold text-foreground">
          {subject}
        </div>
      ) : null}
      <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
        {body_preview}
      </div>
      {rationale ? (
        <div className="mt-2 text-xs text-foreground-mute">
          Why: {rationale}
        </div>
      ) : null}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="draft-use"
          onClick={onUse}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground"
        >
          Use this draft
        </button>
      </div>
    </div>
  );
}
